import React, { createContext, useContext, useReducer, useCallback } from 'react';

// Initial meter values (0-100 scale)
const INITIAL_METERS = {
  trust: 50,
  efficacy: 50,
  clarity: 50,
  escalationRisk: 50,
};

// Prep Card Intent Structure
const INITIAL_PREP_CARD = {
  openingStance: null, // 'inquiry_first', 'complaint_first', 'reassurance_first', 'standards_first'
  needsToUnderstand: [], // ['teacher_account', 'student_impact', 'family_history']
  mustNotAvoid: [], // ['student_impact', 'accountability', 'next_steps']
  equityLens: false, // Whether equity check is selected
  completed: false,
};

// Simulation State
const INITIAL_STATE = {
  // Core meters
  meters: { ...INITIAL_METERS },
  previewMeters: null, // For ghost preview state
  
  // Prep Card (User Intent)
  prepCard: { ...INITIAL_PREP_CARD },
  
  // Current view
  currentView: 'hub', // 'hub', 'theory', 'prep', 'simulation', 'dashboard'
  currentModule: 1, // 1-9 for theory modules
  visitedModules: new Set(), // Track which theory modules have been viewed
  theoryComplete: false, // All 9 modules visited
  
  // Simulation state
  simulation: {
    active: false,
    currentStage: 0, // 0-5
    currentNodeId: null,
    characterState: 'guarded', // 'guarded', 'defensive', 'reflective', 'open', 'escalated'
    pathHistory: [], // Array of chosen node IDs
    meterHistory: [], // Snapshots of meters at each decision
  },
  
  // Coach Mode
  coachNudges: [], // Array of nudge objects { id, type, message, timestamp }
  activeNudge: null,
  
  // Intent Mismatch tracking
  mismatches: {
    hard: [], // Hard mismatch triggers
    soft: [], // Soft mismatch triggers
    softCountInStage: 0, // For escalation rule
  },
  
  // Final outcome
  outcome: null, // 'A', 'B', 'C', 'D', 'E'
  
  // Debrief data
  debrief: null,
};

// Action Types
const ACTIONS = {
  SET_VIEW: 'SET_VIEW',
  SET_MODULE: 'SET_MODULE',
  UPDATE_METERS: 'UPDATE_METERS',
  SET_PREVIEW_METERS: 'SET_PREVIEW_METERS',
  CLEAR_PREVIEW: 'CLEAR_PREVIEW',
  UPDATE_PREP_CARD: 'UPDATE_PREP_CARD',
  COMPLETE_PREP_CARD: 'COMPLETE_PREP_CARD',
  START_SIMULATION: 'START_SIMULATION',
  MAKE_CHOICE: 'MAKE_CHOICE',
  UPDATE_CHARACTER_STATE: 'UPDATE_CHARACTER_STATE',
  ADD_COACH_NUDGE: 'ADD_COACH_NUDGE',
  DISMISS_NUDGE: 'DISMISS_NUDGE',
  RECORD_MISMATCH: 'RECORD_MISMATCH',
  CALCULATE_OUTCOME: 'CALCULATE_OUTCOME',
  RESET_SIMULATION: 'RESET_SIMULATION',
  VISIT_MODULE: 'VISIT_MODULE',
};

// Reducer
function simulationReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_VIEW:
      return { ...state, currentView: action.payload };
      
    case ACTIONS.SET_MODULE:
      return { ...state, currentModule: action.payload };
      
    case ACTIONS.UPDATE_METERS:
      const newMeters = { ...state.meters };
      Object.entries(action.payload).forEach(([key, delta]) => {
        newMeters[key] = Math.max(0, Math.min(100, newMeters[key] + delta));
      });
      return { ...state, meters: newMeters };
      
    case ACTIONS.SET_PREVIEW_METERS:
      const previewMeters = { ...state.meters };
      Object.entries(action.payload).forEach(([key, delta]) => {
        previewMeters[key] = Math.max(0, Math.min(100, previewMeters[key] + delta));
      });
      return { ...state, previewMeters };
      
    case ACTIONS.CLEAR_PREVIEW:
      return { ...state, previewMeters: null };
      
    case ACTIONS.UPDATE_PREP_CARD:
      return {
        ...state,
        prepCard: { ...state.prepCard, ...action.payload },
      };
      
    case ACTIONS.COMPLETE_PREP_CARD:
      return {
        ...state,
        prepCard: { ...state.prepCard, completed: true },
      };
      
    case ACTIONS.START_SIMULATION:
      return {
        ...state,
        currentView: 'simulation',
        simulation: {
          ...INITIAL_STATE.simulation,
          active: true,
          currentNodeId: 'S0_ENTRY',
          meterHistory: [{ ...state.meters }],
        },
      };
      
    case ACTIONS.MAKE_CHOICE:
      const { nodeId, meterDeltas, stage, characterState } = action.payload;
      const updatedMeters = { ...state.meters };
      
      if (meterDeltas) {
        Object.entries(meterDeltas).forEach(([key, delta]) => {
          updatedMeters[key] = Math.max(0, Math.min(100, updatedMeters[key] + delta));
        });
      }
      
      return {
        ...state,
        meters: updatedMeters,
        simulation: {
          ...state.simulation,
          currentStage: stage ?? state.simulation.currentStage,
          currentNodeId: nodeId,
          characterState: characterState ?? state.simulation.characterState,
          pathHistory: [...state.simulation.pathHistory, nodeId],
          meterHistory: [...state.simulation.meterHistory, { ...updatedMeters }],
        },
      };
      
    case ACTIONS.UPDATE_CHARACTER_STATE:
      return {
        ...state,
        simulation: {
          ...state.simulation,
          characterState: action.payload,
        },
      };
      
    case ACTIONS.ADD_COACH_NUDGE:
      const nudge = {
        id: Date.now(),
        ...action.payload,
        timestamp: new Date().toISOString(),
      };
      return {
        ...state,
        coachNudges: [...state.coachNudges, nudge],
        activeNudge: nudge,
      };
      
    case ACTIONS.DISMISS_NUDGE:
      return {
        ...state,
        activeNudge: null,
      };
      
    case ACTIONS.RECORD_MISMATCH:
      const { mismatchType, trigger } = action.payload;
      const newMismatches = { ...state.mismatches };
      
      if (mismatchType === 'hard') {
        newMismatches.hard = [...newMismatches.hard, trigger];
      } else {
        newMismatches.soft = [...newMismatches.soft, trigger];
        newMismatches.softCountInStage += 1;
      }
      
      return { ...state, mismatches: newMismatches };
      
    case ACTIONS.CALCULATE_OUTCOME:
      const { trust, efficacy, clarity, escalationRisk } = state.meters;
      const { hard: hardMismatches } = state.mismatches;
      const hasStrongChoice = action.payload?.hasStrongChoice ?? false;
      
      let outcome = 'B'; // Default: Productive but Incomplete
      
      // Outcome A: Trust Restored, Action Clear
      if (trust >= 65 && clarity >= 60 && escalationRisk <= 40 && 
          hasStrongChoice && hardMismatches.length === 0) {
        outcome = 'A';
      }
      // Outcome C: Relationship Preserved, Accountability Soft
      else if (trust >= 55 && clarity < 50 && escalationRisk < 60) {
        outcome = 'C';
      }
      // Outcome D: Compliance Without Buy-In
      else if (clarity >= 50 && trust < 50 && efficacy < 50) {
        outcome = 'D';
      }
      // Outcome E: Escalation Increased
      else if (escalationRisk >= 65 && trust <= 40 && hardMismatches.length > 0) {
        outcome = 'E';
      }
      
      return {
        ...state,
        outcome,
        currentView: 'dashboard',
        simulation: {
          ...state.simulation,
          active: false, // Deactivate simulation when outcome is calculated
        },
        debrief: generateDebrief(state, outcome),
      };
      
    case ACTIONS.RESET_SIMULATION:
      return {
        ...INITIAL_STATE,
        prepCard: { ...INITIAL_PREP_CARD },
      };

    case ACTIONS.VISIT_MODULE:
      const updatedVisited = new Set(state.visitedModules);
      updatedVisited.add(action.payload);
      return {
        ...state,
        visitedModules: updatedVisited,
        theoryComplete: updatedVisited.size >= 9,
      };
      
    default:
      return state;
  }
}

// Helper function to generate debrief
function generateDebrief(state, outcome) {
  const outcomeDescriptions = {
    A: {
      title: 'Trust Restored, Action Clear',
      summary: 'You successfully built trust with Ms. Ortega while maintaining clarity about expectations. The conversation resulted in a clear, collaborative path forward.',
      finalState: 'Engaged / Reflective',
    },
    B: {
      title: 'Productive but Incomplete',
      summary: 'The conversation moved in a constructive direction, but key elements remain unresolved. Clarity or accountability may need follow-up.',
      finalState: 'Guarded / Reflective',
    },
    C: {
      title: 'Relationship Preserved, Accountability Soft',
      summary: 'You maintained a positive relationship, but the core issue remains under-defined. Follow-up will be needed to establish clear accountability.',
      finalState: 'Engaged / Guarded',
    },
    D: {
      title: 'Compliance Without Buy-In',
      summary: 'While clarity was achieved, the relationship suffered. Ms. Ortega may comply with directives but lacks genuine ownership of the resolution.',
      finalState: 'Guarded / Defensive',
    },
    E: {
      title: 'Escalation Increased',
      summary: 'The conversation became adversarial. Trust has been compromised, and there may be need for further mediation or support.',
      finalState: 'Defensive / Escalated',
    },
  };

  const m = state.meters;
  const alignment = calculatePrepCardAlignment(state);

  // Overall readiness: weighted average of meter health
  const trustScore = m.trust;
  const efficacyScore = m.efficacy;
  const clarityScore = m.clarity;
  const riskScore = Math.max(0, 100 - m.escalationRisk);
  const overallReadiness = Math.round((trustScore * 0.3 + efficacyScore * 0.2 + clarityScore * 0.3 + riskScore * 0.2));

  // Decision quality from path choices
  const pathHistory = state.simulation.pathHistory;
  const strongChoices = pathHistory.filter(p => p.quality === 'strong').length;
  const totalChoices = pathHistory.length;
  const decisionQuality = totalChoices > 0
    ? Math.round((strongChoices / totalChoices) * 100)
    : 0;

  // Top coach signal
  const allNudges = [...state.mismatches.hard, ...state.mismatches.soft];
  const topCoachSignal = state.mismatches.hard.length > 0
    ? { type: 'hard', trigger: state.mismatches.hard[0] }
    : state.mismatches.soft.length > 0
      ? { type: 'soft', trigger: state.mismatches.soft[0] }
      : null;

  // Next practice recommendation
  const weakestArea = [
    { name: 'Trust', value: m.trust },
    { name: 'Efficacy', value: m.efficacy },
    { name: 'Clarity', value: m.clarity },
  ].sort((a, b) => a.value - b.value)[0];

  const practiceRecommendations = {
    Trust: 'Focus on inquiry first moves and perspective-gathering before naming concerns.',
    Efficacy: 'Explore collaborative resolution paths that honor the teacher\'s professional judgment.',
    Clarity: 'Practice naming impact directly and establishing specific, concrete next steps.',
  };

  const nextPractice = {
    area: weakestArea.name,
    value: weakestArea.value,
    recommendation: practiceRecommendations[weakestArea.name],
  };
  
  return {
    ...outcomeDescriptions[outcome],
    finalMeters: { ...state.meters },
    pathHistory: state.simulation.pathHistory,
    mismatches: state.mismatches,
    prepCardAlignment: alignment,
    overallReadiness,
    decisionQuality,
    strongChoices,
    totalChoices,
    topCoachSignal,
    nextPractice,
  };
}

// Calculate how well choices aligned with prep card
function calculatePrepCardAlignment(state) {
  // Simplified alignment calculation
  const totalChoices = state.simulation.pathHistory.length;
  const hardMismatches = state.mismatches.hard.length;
  const softMismatches = state.mismatches.soft.length;
  
  if (totalChoices === 0) return 100;
  
  const penalty = (hardMismatches * 20) + (softMismatches * 5);
  return Math.max(0, 100 - penalty);
}

// Create Context
const SimulationContext = createContext(null);

// Provider Component
export function SimulationProvider({ children }) {
  const [state, dispatch] = useReducer(simulationReducer, INITIAL_STATE);
  
  // Action creators
  const setView = useCallback((view) => {
    dispatch({ type: ACTIONS.SET_VIEW, payload: view });
  }, []);
  
  const setModule = useCallback((module) => {
    dispatch({ type: ACTIONS.SET_MODULE, payload: module });
  }, []);
  
  const updateMeters = useCallback((deltas) => {
    dispatch({ type: ACTIONS.UPDATE_METERS, payload: deltas });
  }, []);
  
  const setPreviewMeters = useCallback((deltas) => {
    dispatch({ type: ACTIONS.SET_PREVIEW_METERS, payload: deltas });
  }, []);
  
  const clearPreview = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_PREVIEW });
  }, []);
  
  const updatePrepCard = useCallback((updates) => {
    dispatch({ type: ACTIONS.UPDATE_PREP_CARD, payload: updates });
  }, []);
  
  const completePrepCard = useCallback(() => {
    dispatch({ type: ACTIONS.COMPLETE_PREP_CARD });
  }, []);
  
  const startSimulation = useCallback(() => {
    dispatch({ type: ACTIONS.START_SIMULATION });
  }, []);
  
  const makeChoice = useCallback((choiceData) => {
    dispatch({ type: ACTIONS.MAKE_CHOICE, payload: choiceData });
  }, []);
  
  const updateCharacterState = useCallback((state) => {
    dispatch({ type: ACTIONS.UPDATE_CHARACTER_STATE, payload: state });
  }, []);
  
  const addCoachNudge = useCallback((nudgeData) => {
    dispatch({ type: ACTIONS.ADD_COACH_NUDGE, payload: nudgeData });
  }, []);
  
  const dismissNudge = useCallback(() => {
    dispatch({ type: ACTIONS.DISMISS_NUDGE });
  }, []);
  
  const recordMismatch = useCallback((mismatchType, trigger) => {
    dispatch({ type: ACTIONS.RECORD_MISMATCH, payload: { mismatchType, trigger } });
  }, []);
  
  const calculateOutcome = useCallback((hasStrongChoice = false) => {
    dispatch({ type: ACTIONS.CALCULATE_OUTCOME, payload: { hasStrongChoice } });
  }, []);
  
  const resetSimulation = useCallback(() => {
    dispatch({ type: ACTIONS.RESET_SIMULATION });
  }, []);

  const visitModule = useCallback((moduleId) => {
    dispatch({ type: ACTIONS.VISIT_MODULE, payload: moduleId });
  }, []);
  
  // Check if a meter is in critical band
  const isMeterCritical = useCallback((meterName) => {
    return state.meters[meterName] <= 29;
  }, [state.meters]);
  
  // Get meter status color
  const getMeterColor = useCallback((meterName) => {
    const value = state.meters[meterName];
    if (meterName === 'escalationRisk') {
      // Inverse: high is bad
      if (value >= 70) return 'red';
      if (value >= 50) return 'yellow';
      return 'green';
    }
    // Normal: low is bad
    if (value <= 29) return 'red';
    if (value <= 49) return 'yellow';
    return 'green';
  }, [state.meters]);
  
  const value = {
    state,
    setView,
    setModule,
    updateMeters,
    setPreviewMeters,
    clearPreview,
    updatePrepCard,
    completePrepCard,
    startSimulation,
    makeChoice,
    updateCharacterState,
    addCoachNudge,
    dismissNudge,
    recordMismatch,
    calculateOutcome,
    resetSimulation,
    visitModule,
    isMeterCritical,
    getMeterColor,
  };
  
  return (
    <SimulationContext.Provider value={value}>
      {children}
    </SimulationContext.Provider>
  );
}

// Custom hook to use the simulation context
export function useSimulation() {
  const context = useContext(SimulationContext);
  if (!context) {
    throw new Error('useSimulation must be used within a SimulationProvider');
  }
  return context;
}

export default SimulationContext;
